import React, { useMemo, useState } from 'react';
import cls from 'classnames';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import GridColumn, { IGridColumns } from './components/GridColumn';
import igdbIcon from '@intelligence/assets/svg/gameInfo/igdb.svg';
import languageCheckSvg from '@intelligence/assets/svg/gameInfo/languagesCheck.svg';
import { languagesMap } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/LanguagesInfoCard/config';
import MoreLanguagesModal from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/LanguagesInfoCard/MoreLanguagesModal';

import './index.scss';

interface ILanguagesInfoCard {
  data: {
    [key: string]: {
      Audio: number;
      Interface: number;
      Subtitles: number;
    };
  };
}

interface ILanguageData {
  language: string;
  Audio: number;
  Interface: number;
  Subtitles: number;
}

const languagesCheckedIcon = <img src={languageCheckSvg} alt="" />;
const languagesUnCheck = (
  <div style={{ fontSize: 12, color: '#838697', lineHeight: '16px' }}>-</div>
);
const renderLanguagesCheck = (checked: number) =>
  checked ? languagesCheckedIcon : languagesUnCheck;

/**
 * PC&Console GameInfo 语言信息卡片
 *
 * @param {ILanguagesInfoCard} param0
 * @param {{ [key: string]: { Audio: number; Interface: number; Subtitles: number; }; }} param0.data
 * @returns {*}
 */
function LanguagesInfoCard({ data }: ILanguagesInfoCard) {
  const { formatMessage, isCN } = useIntlExtend();

  const [showMore, setShowMore] = useState(false);

  // 将数据格式化为可渲染的数据
  const formattedLanguageData = useMemo<ILanguageData[]>(() => {
    const keys = Object.keys(data || {});
    return keys.map((key) => {
      return {
        ...data[key],
        language: isCN ? languagesMap[key] || key : key,
      };
    });
  }, [data, isCN]);

  // 超出 15 行数据时，展示滚动条，右侧 padding 调整
  const overflow = formattedLanguageData?.length >= 16;

  const columns: IGridColumns<ILanguageData> = [
    {
      key: 'language',
      title: 'Languages',
      title_cn: '语言',
      render: (item: ILanguageData) => {
        return (
          <div style={{ fontSize: 12, color: '#525464', lineHeight: '16px' }}>{item.language}</div>
        );
      },
    },
    {
      key: 'Interface',
      title: 'Interface',
      title_cn: '界面',
      align: 'center',
      width: 64,
      width_cn: 30,
      render: (item: ILanguageData) => renderLanguagesCheck(item.Interface),
    },
    {
      key: 'Audio',
      title: 'Full Audio',
      title_cn: '完全音频',
      align: 'center',
      width: 72,
      width_cn: 60,
      render: (item: ILanguageData) => renderLanguagesCheck(item.Audio),
    },
    {
      key: 'Subtitles',
      title: 'Subtitles',
      title_cn: '字幕',
      align: 'center',
      width: 62,
      width_cn: 30,
      render: (item: ILanguageData) => renderLanguagesCheck(item.Subtitles),
    },
  ];

  return (
    <>
      <div
        className={cls('dbi_game_info_languages_card', {
          'no-languages-data': !formattedLanguageData?.length,
        })}
      >
        <div className="dbi_game_info_languages_card_title">
          <div className="dbi_game_info_languages_card_title-left">
            <img className="dbi_game_info_languages_card_title-img" src={igdbIcon} />
            {formatMessage({ id: 'dbi_game_languages' })}
          </div>
          {formattedLanguageData?.length > 10 && (
            <div
              className="dbi_game_info_languages_card_title-right"
              onClick={() => setShowMore(true)}
            >
              {formatMessage({ id: 'dbi_more' })}
            </div>
          )}
        </div>
        <div className={cls('dbi_game_info_languages_card_content', { overflow })}>
          <GridColumn columns={columns} data={formattedLanguageData?.slice(0, 10)} />
        </div>
      </div>

      <MoreLanguagesModal
        visible={showMore}
        setVisible={setShowMore}
        content={
          <div className={cls('dbi_game_info_languages_card_content', { overflow })}>
            <GridColumn columns={columns} data={formattedLanguageData} />
          </div>
        }
      />
    </>
  );
}

export default LanguagesInfoCard;
